import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import JobCodeSection from '../components/JobCodeSection';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import InstallationChecklistCards from '../components/InstallationChecklistCards';
import { type ChecklistAttachmentApi } from '../components/SalesChecklistCards';
import {
  addInstallationProduct,
  assignInstallationProductUnit,
  completeInstallationAppointment,
  createInstallationNote,
  voidInstallationChecklistAttachment,
  unvoidInstallationChecklistAttachment,
  deleteInstallationProduct,
  fetchInstallationAppointment,
  fetchInstallationChecklistAttachments,
  fetchInstallationNotes,
  fetchInstallationCustomerNotes,
  hasOpenConflict,
  installationChecklistAttachmentDownloadUrl,
  rescheduleInstallationAppointment,
  updateInstallationAppointment,
  uploadInstallationChecklistAttachment,
  voidInstallationAppointment,
  type InstallationAppointmentRow,
  type InstallationNoteRow,
  type InstallationCustomerNoteRow,
  type InstallationProductRow,
} from '../api/installationAppointments';
import { createCustomerSignoff } from '../api/customerSignoffs';
import {
  fetchInventoryProducts,
  fetchInventoryUnits,
  type InventoryProductRow,
  type InventoryUnitRow,
} from '../api/inventoryApi';
import {
  INSTALLATION_CHECKLIST_SECTIONS,
  isInstallationChecklistItemComplete,
  normalizeInstallationChecklistState,
  type InstallationChecklistPhotoCounts,
  type InstallationChecklistState,
} from '../data/installationChecklist';
import './Page.css';
import './Events.css';
import './Elevators.css';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Robot and Accessory catalog types are serialized; Stock Item is not. */
function productTracksSerial(type: string | undefined): boolean {
  return type === 'Robot' || type === 'Accessory';
}

const InstallationAppointmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const idNum = id ? parseInt(id, 10) : NaN;

  const [appt, setAppt] = useState<InstallationAppointmentRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [checklist, setChecklist] = useState<InstallationChecklistState>({});
  const checklistRef = useRef<InstallationChecklistState>({});
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [checklistCollapsed, setChecklistCollapsed] = useState(false);
  const [photoCounts, setPhotoCounts] = useState<InstallationChecklistPhotoCounts>({});

  const [actionBusy, setActionBusy] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState('');
  const [resolveOpen, setResolveOpen] = useState(false);
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [rescheduleEndAt, setRescheduleEndAt] = useState('');
  const [rescheduleMultiDay, setRescheduleMultiDay] = useState(false);
  const [resolveError, setResolveError] = useState('');

  // Inventory catalog for the add-product flow.
  const [invProducts, setInvProducts] = useState<InventoryProductRow[]>([]);
  const [invUnits, setInvUnits] = useState<InventoryUnitRow[]>([]);

  // Add / assign-unit product modal.
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<InstallationProductRow | null>(null);
  const [modalProductId, setModalProductId] = useState('');
  const [modalUnitId, setModalUnitId] = useState('');
  const [modalQty, setModalQty] = useState('1');
  const [modalError, setModalError] = useState('');
  const [modalBusy, setModalBusy] = useState(false);

  // Notes.
  const [notes, setNotes] = useState<InstallationNoteRow[]>([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState('');

  // Customer notes (client schedule-conflict submissions).
  const [customerNotes, setCustomerNotes] = useState<InstallationCustomerNoteRow[]>([]);

  // Complete confirmation (lists incomplete checklist fields) + resulting sign-off.
  const [completeOpen, setCompleteOpen] = useState(false);
  const [signoffLink, setSignoffLink] = useState<{ id: number } | null>(null);

  // Section-referenced note modal (opened from a checklist section's "Add note").
  const [sectionNoteTarget, setSectionNoteTarget] = useState<{ id: string; title: string } | null>(null);
  const [sectionNoteDraft, setSectionNoteDraft] = useState('');
  const [sectionNoteBusy, setSectionNoteBusy] = useState(false);
  const [sectionNoteError, setSectionNoteError] = useState('');

  const readOnly = appt != null && appt.status !== 'Scheduled';

  // Photo backend for the checklist's picture items (the CR202 photo).
  const checklistAttachmentApi = useMemo<ChecklistAttachmentApi>(
    () => ({
      fetch: () => fetchInstallationChecklistAttachments(idNum),
      upload: (sectionId, file, uid) =>
        uploadInstallationChecklistAttachment(idNum, sectionId, file, uid ?? user?.id ?? null),
      void: (attachmentId, reason, uid) =>
        voidInstallationChecklistAttachment(idNum, attachmentId, reason, uid ?? user?.id ?? null),
      unvoid: (attachmentId, uid) =>
        unvoidInstallationChecklistAttachment(idNum, attachmentId, uid ?? user?.id ?? null),
      downloadUrl: (attachmentId) => installationChecklistAttachmentDownloadUrl(idNum, attachmentId),
    }),
    [idNum, user?.id]
  );

  const loadNotes = useCallback(async () => {
    if (!Number.isInteger(idNum)) return;
    try {
      setNotes(await fetchInstallationNotes(idNum));
    } catch {
      /* non-fatal */
    }
  }, [idNum]);

  const loadCustomerNotes = useCallback(async () => {
    if (!Number.isInteger(idNum)) return;
    try {
      setCustomerNotes(await fetchInstallationCustomerNotes(idNum));
    } catch {
      /* non-fatal */
    }
  }, [idNum]);

  const refreshInventory = useCallback(async () => {
    try {
      const [p, u] = await Promise.all([fetchInventoryProducts(), fetchInventoryUnits()]);
      setInvProducts(p);
      setInvUnits(u);
    } catch {
      /* non-fatal */
    }
  }, []);

  const load = useCallback(async () => {
    if (!Number.isInteger(idNum) || idNum < 1) {
      setError('Invalid appointment.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const row = await fetchInstallationAppointment(idNum);
      setAppt(row);
      const cd = normalizeInstallationChecklistState(row.checklist_state);
      setChecklist(cd);
      checklistRef.current = cd;
      setChecklistCollapsed(row.status !== 'Scheduled');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load appointment.');
      setAppt(null);
    } finally {
      setLoading(false);
    }
  }, [idNum]);

  useEffect(() => {
    void load();
    void loadNotes();
    void loadCustomerNotes();
    void refreshInventory();
  }, [load, loadNotes, loadCustomerNotes, refreshInventory]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const doSave = useCallback(async () => {
    if (!Number.isInteger(idNum)) return;
    setSaveState('saving');
    try {
      await updateInstallationAppointment(idNum, { checklist_state: checklistRef.current });
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }, [idNum]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void doSave(), 700);
  }, [doSave]);

  const setChecklistValue = useCallback(
    (key: string, value: string | boolean) => {
      setChecklist((prev) => {
        const next = { ...prev, [key]: value };
        checklistRef.current = next;
        return next;
      });
      setSaveState('saving');
      scheduleSave();
    },
    [scheduleSave]
  );

  const flushChecklist = useCallback(async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      await updateInstallationAppointment(idNum, { checklist_state: checklistRef.current });
    } catch {
      /* best effort */
    }
  }, [idNum]);

  // Checklist entries still outstanding: empty fields, unchecked boxes, missing pictures.
  const incompleteFields = useMemo(() => {
    const out: string[] = [];
    for (const section of INSTALLATION_CHECKLIST_SECTIONS) {
      for (const item of section.items) {
        if (isInstallationChecklistItemComplete(item, checklist, photoCounts)) continue;
        out.push(`${section.title.replace(/^\d+\.\s*/, '')} — ${item.label}`);
      }
    }
    return out;
  }, [checklist, photoCounts]);

  const confirmComplete = async () => {
    if (!appt) return;
    setActionBusy(true);
    try {
      await flushChecklist();
      const updated = await completeInstallationAppointment(appt.id);
      setAppt(updated);
      setChecklistCollapsed(true);
      // Create a pending customer sign-off for the Customer Sign-Offs tab.
      try {
        const signoff = await createCustomerSignoff({
          installation_appointment_id: appt.id,
          client_id: appt.client_id,
          business_name: appt.business_name,
          created_by_user_id: user?.id,
        });
        setSignoffLink({ id: signoff.id });
      } catch (soErr) {
        console.error('Failed to create customer sign-off:', soErr);
      }
      setCompleteOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to complete appointment.');
      setCompleteOpen(false);
    } finally {
      setActionBusy(false);
    }
  };

  const submitVoid = async () => {
    if (!appt) return;
    const reason = voidReason.trim();
    if (!reason) {
      setVoidError('Please provide a reason for voiding.');
      return;
    }
    setActionBusy(true);
    setVoidError('');
    try {
      await flushChecklist();
      const updated = await voidInstallationAppointment(appt.id, reason, user?.id);
      setAppt(updated);
      setVoidOpen(false);
      setVoidReason('');
      void loadNotes();
    } catch (e) {
      setVoidError(e instanceof Error ? e.message : 'Failed to void appointment.');
    } finally {
      setActionBusy(false);
    }
  };

  const submitReschedule = async () => {
    if (!appt) return;
    if (!rescheduleAt) {
      setResolveError('Please choose a new date and time.');
      return;
    }
    if (rescheduleMultiDay && !rescheduleEndAt) {
      setResolveError('Enter when the installation ends, or switch back to a single day.');
      return;
    }
    setActionBusy(true);
    setResolveError('');
    try {
      const updated = await rescheduleInstallationAppointment(
        appt.id,
        new Date(rescheduleAt).toISOString(),
        rescheduleMultiDay ? new Date(rescheduleEndAt).toISOString() : null
      );
      setAppt(updated);
      setResolveOpen(false);
      setRescheduleAt('');
      setRescheduleEndAt('');
      setRescheduleMultiDay(false);
    } catch (e) {
      setResolveError(e instanceof Error ? e.message : 'Failed to reschedule.');
    } finally {
      setActionBusy(false);
    }
  };

  const dismissConflict = async () => {
    if (!appt) return;
    setActionBusy(true);
    setResolveError('');
    try {
      const updated = await updateInstallationAppointment(appt.id, { resolve_conflict: true });
      setAppt(updated);
      setResolveOpen(false);
    } catch (e) {
      setResolveError(e instanceof Error ? e.message : 'Failed to resolve conflict.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleReopen = async () => {
    if (!appt) return;
    if (!window.confirm('Reopen this appointment for editing? It will move back to Scheduled.')) return;
    setActionBusy(true);
    try {
      const updated = await updateInstallationAppointment(appt.id, { status: 'Scheduled' });
      setAppt(updated);
      setChecklistCollapsed(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reopen appointment.');
    } finally {
      setActionBusy(false);
    }
  };

  // ── Product modal ──
  const openAddProduct = () => {
    setAssignTarget(null);
    setModalProductId('');
    setModalUnitId('');
    setModalQty('1');
    setModalError('');
    setProductModalOpen(true);
  };

  const openAssignUnit = (row: InstallationProductRow) => {
    setAssignTarget(row);
    // Best-effort match the catalog product by name so we can list its units.
    const match = invProducts.find((p) => p.name === row.product_name);
    setModalProductId(match?.id ?? '');
    setModalUnitId('');
    setModalError('');
    setProductModalOpen(true);
  };

  const modalProduct = useMemo(
    () => invProducts.find((p) => p.id === modalProductId) ?? null,
    [invProducts, modalProductId]
  );
  const modalTracksSerial = assignTarget != null || productTracksSerial(modalProduct?.type);
  const availableUnits = useMemo(() => {
    if (!modalProductId) return [];
    return invUnits.filter((u) => u.productId === modalProductId && u.status === 'In Storage');
  }, [invUnits, modalProductId]);

  const submitProductModal = async () => {
    if (!appt) return;
    setModalBusy(true);
    setModalError('');
    try {
      if (assignTarget) {
        if (!modalUnitId) {
          setModalError('Select a unit from the database.');
          setModalBusy(false);
          return;
        }
        await assignInstallationProductUnit(appt.id, assignTarget.id, modalUnitId);
      } else {
        if (!modalProduct) {
          setModalError('Choose a product type.');
          setModalBusy(false);
          return;
        }
        if (modalTracksSerial && !modalUnitId) {
          setModalError('This product type is serialized — select a specific unit from the database.');
          setModalBusy(false);
          return;
        }
        await addInstallationProduct(appt.id, {
          product_id: modalProduct.id,
          product_name: modalProduct.name,
          unit_id: modalTracksSerial ? modalUnitId : null,
          quantity: modalTracksSerial ? 1 : Math.max(1, parseInt(modalQty, 10) || 1),
          tracks_serial: modalTracksSerial,
        });
      }
      setProductModalOpen(false);
      const [row] = await Promise.all([fetchInstallationAppointment(appt.id), refreshInventory()]);
      setAppt(row);
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Failed to save product.');
    } finally {
      setModalBusy(false);
    }
  };

  const removeProduct = async (row: InstallationProductRow) => {
    if (!appt) return;
    if (!window.confirm(`Remove ${row.product_name}?${row.unit_id ? ' The unit will return to In Storage.' : ''}`)) return;
    try {
      await deleteInstallationProduct(appt.id, row.id);
      const [fresh] = await Promise.all([fetchInstallationAppointment(appt.id), refreshInventory()]);
      setAppt(fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove product.');
    }
  };

  const submitNote = async () => {
    if (!appt || !user?.id) return;
    const text = noteDraft.trim();
    if (!text) return;
    setNoteBusy(true);
    setNoteError('');
    try {
      await createInstallationNote(appt.id, user.id, text);
      setNoteDraft('');
      await loadNotes();
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : 'Failed to add note.');
    } finally {
      setNoteBusy(false);
    }
  };

  const openSectionNote = useCallback((section: { id: string; title: string }) => {
    setSectionNoteTarget(section);
    setSectionNoteDraft('');
    setSectionNoteError('');
  }, []);

  const submitSectionNote = async () => {
    if (!appt || !user?.id || !sectionNoteTarget) return;
    const text = sectionNoteDraft.trim();
    if (!text) {
      setSectionNoteError('Please enter a note.');
      return;
    }
    setSectionNoteBusy(true);
    setSectionNoteError('');
    try {
      await createInstallationNote(appt.id, user.id, text, sectionNoteTarget);
      setSectionNoteTarget(null);
      setSectionNoteDraft('');
      await loadNotes();
    } catch (e) {
      setSectionNoteError(e instanceof Error ? e.message : 'Failed to add note.');
    } finally {
      setSectionNoteBusy(false);
    }
  };

  const saveStatusText = useMemo(() => {
    if (readOnly) return '';
    switch (saveState) {
      case 'saving':
        return 'Saving…';
      case 'saved':
        return 'All changes saved';
      case 'error':
        return 'Could not save changes — check your connection';
      default:
        return '';
    }
  }, [saveState, readOnly]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <PageHeader title="Installation appointment" />
            <div className="page-content">
              <p className="page-subtitle">Loading…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error || !appt) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <PageHeader title="Installation appointment" />
            <div className="page-content">
              <p className="create-order-error" role="alert">
                {error || 'Appointment not found.'}
              </p>
              <button type="button" className="page-toolbar-btn" onClick={() => navigate('/installation')}>
                ← Back to Installation
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const statusClass =
    appt.status === 'Completed'
      ? 'elevator-status-badge--completed'
      : appt.status === 'Void'
      ? 'elevator-status-badge--void'
      : 'elevator-status-badge--scheduled';

  const needsSerialCount = appt.products.filter((p) => p.needs_serial).length;

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader title="Installation appointment" subtitle="Installation & deployment checklist" />
          <div className="page-content">
            <button type="button" className="elevator-back-link" onClick={() => navigate('/installation')}>
              ← Back to Installation
            </button>

            <div className="elevator-detail-title-row">
              <h1 className="project-detail-title" style={{ margin: 0 }}>
                {appt.business_name}
                <span className={`elevator-status-badge ${statusClass}`}>{appt.status}</span>
              </h1>
              <div className="elevator-detail-actions">
                {appt.status === 'Scheduled' ? (
                  <>
                    <button
                      type="button"
                      className="elevator-btn elevator-btn--complete"
                      disabled={actionBusy}
                      onClick={() => setCompleteOpen(true)}
                    >
                      {actionBusy ? 'Working…' : 'Complete'}
                    </button>
                    <button
                      type="button"
                      className="elevator-btn elevator-btn--void"
                      disabled={actionBusy}
                      onClick={() => {
                        setVoidReason('');
                        setVoidError('');
                        setVoidOpen(true);
                      }}
                    >
                      Void
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="elevator-btn elevator-btn--reopen"
                    disabled={actionBusy}
                    onClick={() => void handleReopen()}
                  >
                    {actionBusy ? 'Working…' : 'Reopen'}
                  </button>
                )}
              </div>
            </div>

            {appt.status === 'Void' ? (
              <div className="elevator-void-banner" role="status">
                <strong>This appointment was voided.</strong>
                Reason: {appt.void_reason || '—'}
                {appt.voided_at ? ` · ${formatDateTime(appt.voided_at)}` : ''}
              </div>
            ) : null}
            {appt.status === 'Completed' ? (
              <div className="elevator-completed-banner" role="status">
                Completed{appt.completed_at ? ` on ${formatDateTime(appt.completed_at)}` : ''}. This record is read-only.
                Use “Reopen” to make changes.
              </div>
            ) : null}

            {hasOpenConflict(appt) && appt.status === 'Scheduled' ? (
              <div className="appt-conflict-banner" role="alert">
                <span className="appt-conflict-banner-text">
                  <strong>Scheduling conflict reported{appt.conflict_author_name ? ` by ${appt.conflict_author_name}` : ''}:</strong>{' '}
                  {appt.conflict_reason || '—'}
                </span>
                <button
                  type="button"
                  className="appt-conflict-resolve-btn"
                  disabled={actionBusy}
                  onClick={() => {
                    setRescheduleAt('');
                    setResolveError('');
                    setResolveOpen(true);
                  }}
                >
                  Resolve
                </button>
              </div>
            ) : null}

            <JobCodeSection jobCode={appt.job_code} clientId={appt.client_id} />

            <section className="elevator-info-card">
              <div className="elevator-info-grid">
                <InfoItem label="Business Name" value={appt.business_name} />
                <InfoItem label="Business Address" value={appt.business_address} />
                <InfoItem label="Point of Contact" value={appt.point_of_contact} />
                <InfoItem label="Contact Number" value={appt.poc_phone} />
                <InfoItem label="Contact Email" value={appt.poc_email} />
                <InfoItem
                  label={appt.installation_end_at ? 'Installation Window' : 'Date & Time of Installation'}
                  value={
                    appt.installation_end_at
                      ? `${formatDateTime(appt.installation_at)} – ${formatDateTime(appt.installation_end_at)}`
                      : formatDateTime(appt.installation_at)
                  }
                />
                <InfoItem label="Created by" value={appt.created_by_username} />
                <InfoItem
                  label="Installer(s)"
                  value={appt.assignees.length > 0 ? appt.assignees.map((a) => a.username).join(', ') : null}
                />
                {appt.task_id != null ? (
                  <div className="elevator-info-item">
                    <span className="elevator-info-label">Task</span>
                    <span className="elevator-info-value">
                      <button type="button" className="elevator-back-link" onClick={() => navigate(`/tasks/${appt.task_id}`)}>
                        View task
                      </button>
                    </span>
                  </div>
                ) : null}
                {appt.order_number ? (
                  <div className="elevator-info-item">
                    <span className="elevator-info-label">Order</span>
                    <span className="elevator-info-value">
                      <button
                        type="button"
                        className="elevator-back-link"
                        onClick={() => navigate(`/orders/${encodeURIComponent(appt.order_number as string)}`)}
                      >
                        {appt.order_number}
                      </button>
                    </span>
                  </div>
                ) : null}
              </div>
            </section>

            {!readOnly ? (
              <p
                className={`elevator-save-status${
                  saveState === 'saving'
                    ? ' elevator-save-status--saving'
                    : saveState === 'saved'
                    ? ' elevator-save-status--saved'
                    : saveState === 'error'
                    ? ' elevator-save-status--error'
                    : ''
                }`}
                role="status"
                aria-live="polite"
              >
                {saveStatusText}
              </p>
            ) : null}

            {/* Installation & deployment checklist (collapsible) */}
            <section className="elevator-form-section">
              <button
                type="button"
                className="install-collapse-header"
                onClick={() => setChecklistCollapsed((c) => !c)}
                aria-expanded={!checklistCollapsed}
              >
                <span className="elevator-form-section-title" style={{ margin: 0 }}>
                  Installation &amp; Deployment Checklist
                </span>
                <span className={`install-collapse-arrow${checklistCollapsed ? '' : ' install-collapse-arrow--open'}`}>▶</span>
              </button>
              {!checklistCollapsed ? (
                <div className="install-checklist-body">
                  <InstallationChecklistCards
                    state={checklist}
                    onItemChange={setChecklistValue}
                    disabled={readOnly}
                    userId={user?.id ?? null}
                    attachmentApi={checklistAttachmentApi}
                    onPhotoCountsChange={setPhotoCounts}
                    onAddSectionNote={user?.id ? openSectionNote : undefined}
                  />
                </div>
              ) : null}
            </section>

            {/* Products deployed at the installation */}
            <section className="elevator-form-section">
              <div className="install-products-header">
                <h2 className="elevator-form-section-title" style={{ margin: 0 }}>
                  Products
                </h2>
                {!readOnly ? (
                  <button type="button" className="elevator-table-add" onClick={openAddProduct}>
                    + Add product
                  </button>
                ) : null}
              </div>
              {needsSerialCount > 0 ? (
                <div className="elevator-completed-banner" role="status" style={{ background: '#fef3c7', color: '#92400e' }}>
                  {needsSerialCount} product{needsSerialCount === 1 ? '' : 's'} brought over from the order still{' '}
                  {needsSerialCount === 1 ? 'needs' : 'need'} a unit selected from the database. Use “Select unit”.
                </div>
              ) : null}
              <div className="elevator-table-wrap">
                <table className="elevator-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Serial number</th>
                      <th>Qty</th>
                      <th>Status</th>
                      {!readOnly ? <th aria-label="Actions" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {appt.products.length === 0 ? (
                      <tr>
                        <td colSpan={readOnly ? 4 : 5} className="events-empty-cell">
                          No products added yet.
                        </td>
                      </tr>
                    ) : (
                      appt.products.map((p) => (
                        <tr key={p.id}>
                          <td data-label="Product">{p.product_name}</td>
                          <td data-label="Serial number">
                            {p.tracks_serial ? (
                              p.serial_number ? (
                                p.serial_number
                              ) : (
                                <span className="signature-pad-empty">Needs unit</span>
                              )
                            ) : (
                              '—'
                            )}
                          </td>
                          <td data-label="Qty">{p.tracks_serial ? 1 : p.quantity}</td>
                          <td data-label="Status">
                            {p.needs_serial ? (
                              <span className="install-pill install-pill--pending">Awaiting unit</span>
                            ) : p.tracks_serial ? (
                              <span className="install-pill install-pill--deployed">Deployed</span>
                            ) : (
                              <span className="install-pill">Added</span>
                            )}
                          </td>
                          {!readOnly ? (
                            <td>
                              <div className="install-row-actions">
                                {p.needs_serial ? (
                                  <button type="button" className="elevator-sign-btn" onClick={() => openAssignUnit(p)}>
                                    Select unit
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  className="elevator-table-remove"
                                  aria-label="Remove product"
                                  onClick={() => void removeProduct(p)}
                                >
                                  ×
                                </button>
                              </div>
                            </td>
                          ) : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Notes */}
            <section className="elevator-form-section">
              <h2 className="elevator-form-section-title">Notes</h2>
              <p className="elevator-form-section-desc">
                The creator and installers are emailed when a note is added.
              </p>
              <div className="install-notes-list">
                {notes.length === 0 ? (
                  <p className="page-subtitle" style={{ margin: 0 }}>
                    No notes yet.
                  </p>
                ) : (
                  notes.map((n) => (
                    <div className="install-note" key={n.id}>
                      <div className="install-note-meta">
                        <span className="install-note-author">{n.submitted_by ?? 'Unknown'}</span>
                        {n.section_title ? (
                          <span className="install-note-section">Re: {n.section_title}</span>
                        ) : null}
                        <span className="install-note-time">{formatDateTime(n.created_at)}</span>
                      </div>
                      <div className="install-note-text">{n.note}</div>
                    </div>
                  ))
                )}
              </div>
              <div className="install-note-composer">
                <textarea
                  className="elevator-text-input elevator-textarea"
                  rows={3}
                  placeholder="Add a note…"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  disabled={noteBusy}
                />
                {noteError ? (
                  <p className="create-order-error" role="alert">
                    {noteError}
                  </p>
                ) : null}
                <div className="modal-actions" style={{ padding: 0, marginTop: '0.5rem' }}>
                  <button
                    type="button"
                    className="update-button"
                    disabled={noteBusy || !noteDraft.trim()}
                    onClick={() => void submitNote()}
                  >
                    {noteBusy ? 'Posting…' : 'Add note'}
                  </button>
                </div>
              </div>
            </section>

            {/* Customer notes: schedule-conflict submissions from the client */}
            <section className="elevator-form-section">
              <h2 className="elevator-form-section-title">Customer Notes</h2>
              <p className="elevator-form-section-desc">
                Messages the client submits via the “Schedule Conflict” link in their scheduling email appear here.
              </p>
              {appt.public_token ? (
                <p className="elevators-confirm-hint" style={{ marginTop: 0 }}>
                  Conflict link: <code>{`${window.location.origin}/installation-conflict/${appt.public_token}`}</code>
                </p>
              ) : null}
              <div className="install-notes-list">
                {customerNotes.length === 0 ? (
                  <p className="page-subtitle" style={{ margin: 0 }}>
                    No customer notes yet.
                  </p>
                ) : (
                  customerNotes.map((n) => (
                    <div className="install-note" key={n.id}>
                      <div className="install-note-meta">
                        <span className="install-note-author">{n.author_name || 'Customer'}</span>
                        <span className="install-note-section">
                          {n.kind === 'schedule_conflict' ? 'Schedule conflict' : n.kind}
                        </span>
                        <span className="install-note-time">{formatDateTime(n.created_at)}</span>
                      </div>
                      <div className="install-note-text">{n.note}</div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {signoffLink ? (
              <div className="elevator-completed-banner" role="status">
                A pending customer sign-off was created.{' '}
                <button
                  type="button"
                  className="elevator-back-link"
                  onClick={() => navigate(`/installation/sign-off/${signoffLink.id}`)}
                >
                  Open sign-off
                </button>
              </div>
            ) : null}
          </div>
        </main>
      </div>

      {/* Complete confirmation modal (lists incomplete checklist fields) */}
      <Modal isOpen={completeOpen} onClose={() => setCompleteOpen(false)} title="Complete installation?" narrow>
        <div className="modal-body">
          {incompleteFields.length > 0 ? (
            <>
              <p style={{ margin: '0 0 0.75rem', color: '#92400e', fontWeight: 600 }}>
                Are you sure? The following {incompleteFields.length} field
                {incompleteFields.length === 1 ? ' is' : 's are'} not completed:
              </p>
              <ul style={{ margin: '0 0 1rem', paddingLeft: '1.2rem', maxHeight: 260, overflowY: 'auto' }}>
                {incompleteFields.map((f) => (
                  <li key={f} style={{ marginBottom: '0.25rem', color: '#374151', fontSize: '0.9rem' }}>
                    {f}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p style={{ margin: '0 0 1rem', color: '#374151' }}>All checklist fields are completed.</p>
          )}
          <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem' }}>
            {appt.order_number
              ? 'Completing will also complete the linked task and order, and create a pending customer sign-off. The record becomes read-only.'
              : 'Completing will create a pending customer sign-off and make this record read-only.'}
          </p>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => setCompleteOpen(false)}>
            Cancel
          </button>
          <button type="button" className="elevator-btn elevator-btn--complete" disabled={actionBusy} onClick={() => void confirmComplete()}>
            {actionBusy ? 'Completing…' : incompleteFields.length > 0 ? 'Complete anyway' : 'Complete'}
          </button>
        </div>
      </Modal>

      {/* Void modal */}
      <Modal isOpen={voidOpen} onClose={() => setVoidOpen(false)} title="Void appointment" narrow>
        <div className="modal-body">
          <p style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.9375rem' }}>
            Please provide a reason for voiding this appointment. It is shown at the top of the appointment, added to the
            notes, and recorded on the linked task.
          </p>
          <div className="form-group">
            <label htmlFor="install-void-reason" className="form-label">
              Reason
            </label>
            <textarea
              id="install-void-reason"
              className="form-input"
              rows={4}
              value={voidReason}
              onChange={(e) => {
                setVoidReason(e.target.value);
                setVoidError('');
              }}
              disabled={actionBusy}
            />
          </div>
          {voidError ? (
            <p className="create-order-error" role="alert">
              {voidError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => setVoidOpen(false)}>
            Cancel
          </button>
          <button type="button" className="elevator-btn elevator-btn--void" disabled={actionBusy} onClick={() => void submitVoid()}>
            {actionBusy ? 'Voiding…' : 'Void appointment'}
          </button>
        </div>
      </Modal>

      {/* Resolve conflict modal */}
      <Modal isOpen={resolveOpen} onClose={() => setResolveOpen(false)} title="Resolve scheduling conflict" narrow>
        <div className="modal-body">
          <p style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.9375rem' }}>
            The client reported a conflict with the current time. Reschedule to a new date/time, void the appointment, or
            mark the conflict as handled.
          </p>
          <div className="form-group">
            <label htmlFor="install-reschedule-at" className="form-label">
              {rescheduleMultiDay ? 'New start date and time' : 'New date and time'}
            </label>
            <input
              id="install-reschedule-at"
              type="datetime-local"
              className="form-input"
              value={rescheduleAt}
              onChange={(e) => {
                setRescheduleAt(e.target.value);
                setResolveError('');
              }}
              disabled={actionBusy}
            />
          </div>
          <div className="form-group">
            <label className="elevators-radio-label">
              <input
                type="checkbox"
                checked={rescheduleMultiDay}
                onChange={(e) => {
                  setRescheduleMultiDay(e.target.checked);
                  if (!e.target.checked) setRescheduleEndAt('');
                  setResolveError('');
                }}
                disabled={actionBusy}
              />
              This installation spans a date range
            </label>
          </div>
          {rescheduleMultiDay ? (
            <div className="form-group">
              <label htmlFor="install-reschedule-end-at" className="form-label">
                New end date and time
              </label>
              <input
                id="install-reschedule-end-at"
                type="datetime-local"
                className="form-input"
                value={rescheduleEndAt}
                onChange={(e) => {
                  setRescheduleEndAt(e.target.value);
                  setResolveError('');
                }}
                disabled={actionBusy}
              />
            </div>
          ) : null}
          {resolveError ? (
            <p className="create-order-error" role="alert">
              {resolveError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          <button
            type="button"
            className="elevator-btn elevator-btn--void"
            disabled={actionBusy}
            onClick={() => {
              setResolveOpen(false);
              setVoidReason('');
              setVoidError('');
              setVoidOpen(true);
            }}
          >
            Void instead
          </button>
          <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => void dismissConflict()}>
            Mark handled
          </button>
          <button type="button" className="update-button" disabled={actionBusy} onClick={() => void submitReschedule()}>
            {actionBusy ? 'Rescheduling…' : 'Reschedule'}
          </button>
        </div>
      </Modal>

      {/* Add / assign product modal */}
      <Modal
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        title={assignTarget ? `Select unit — ${assignTarget.product_name}` : 'Add product'}
        narrow
      >
        <div className="modal-body">
          {!assignTarget ? (
            <div className="form-group">
              <label className="form-label" htmlFor="install-product-type">
                Product type
              </label>
              <select
                id="install-product-type"
                className="form-input"
                value={modalProductId}
                onChange={(e) => {
                  setModalProductId(e.target.value);
                  setModalUnitId('');
                  setModalError('');
                }}
                disabled={modalBusy}
              >
                <option value="">Choose a product…</option>
                {invProducts
                  .filter((p) => p.catalogStatus !== 'Void')
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.type})
                    </option>
                  ))}
              </select>
            </div>
          ) : null}

          {modalTracksSerial ? (
            <div className="form-group">
              <label className="form-label" htmlFor="install-unit">
                Unit (from database)
              </label>
              <select
                id="install-unit"
                className="form-input"
                value={modalUnitId}
                onChange={(e) => {
                  setModalUnitId(e.target.value);
                  setModalError('');
                }}
                disabled={modalBusy || !modalProductId}
              >
                <option value="">{modalProductId ? 'Choose an available unit…' : 'Select a product first'}</option>
                {availableUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.serialNumber || u.id}
                    {u.location ? ` · ${u.location}` : ''}
                  </option>
                ))}
              </select>
              {modalProductId && availableUnits.length === 0 ? (
                <p className="elevators-confirm-hint">No units In Storage for this product.</p>
              ) : null}
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label" htmlFor="install-qty">
                Quantity
              </label>
              <input
                id="install-qty"
                type="number"
                min={1}
                className="form-input"
                value={modalQty}
                onChange={(e) => setModalQty(e.target.value)}
                disabled={modalBusy}
              />
            </div>
          )}

          {modalTracksSerial ? (
            <p className="elevators-confirm-hint">
              Selecting a unit marks it Deployed and sets its Business, Location (city), and Installation Date from this
              appointment.
            </p>
          ) : null}

          {modalError ? (
            <p className="create-order-error" role="alert">
              {modalError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={modalBusy} onClick={() => setProductModalOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={modalBusy} onClick={() => void submitProductModal()}>
            {modalBusy ? 'Saving…' : assignTarget ? 'Assign unit' : 'Add product'}
          </button>
        </div>
      </Modal>

      {/* Section-referenced note modal (opened from a checklist section) */}
      <Modal
        isOpen={sectionNoteTarget != null}
        onClose={() => setSectionNoteTarget(null)}
        title={sectionNoteTarget ? `Add note — ${sectionNoteTarget.title}` : 'Add note'}
        narrow
      >
        <div className="modal-body">
          <p style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.9375rem' }}>
            This note will be added to the appointment’s notes with a reference to{' '}
            <strong>{sectionNoteTarget?.title}</strong>. The creator and installers are emailed.
          </p>
          <div className="form-group">
            <label htmlFor="section-note-text" className="form-label">
              Note
            </label>
            <textarea
              id="section-note-text"
              className="form-input"
              rows={4}
              value={sectionNoteDraft}
              onChange={(e) => {
                setSectionNoteDraft(e.target.value);
                setSectionNoteError('');
              }}
              disabled={sectionNoteBusy}
              placeholder="Add a note about this section…"
            />
          </div>
          {sectionNoteError ? (
            <p className="create-order-error" role="alert">
              {sectionNoteError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            disabled={sectionNoteBusy}
            onClick={() => setSectionNoteTarget(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="update-button"
            disabled={sectionNoteBusy || !sectionNoteDraft.trim()}
            onClick={() => void submitSectionNote()}
          >
            {sectionNoteBusy ? 'Posting…' : 'Add note'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

function InfoItem({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="elevator-info-item">
      <span className="elevator-info-label">{label}</span>
      <span className="elevator-info-value">{value && value.trim() ? value : '—'}</span>
    </div>
  );
}

export default InstallationAppointmentDetail;
